import { ChatLines as ChatLinesIcon, UserPlus as UserPlusIcon } from 'iconoir-react'
import { startCase } from 'lodash'
import type { ReactElement } from 'react'
import { splitByDigits, splitByWords, type Segment } from './highlight'
import type { PageRow } from './pageRegistry'
import type { PaletteQuery } from './paletteSearch'
import { counterpartPhone, type PaletteThread } from './paletteThreads'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { Activity, Contact } from 'app/generated/graphql'
import { getActivityTypeIcon } from 'app/table-columns/activities'
import { activityPersonLabel, activityWhenLabel, messageSnippet } from 'lib/paletteLabels'

function Highlighted({ segments }: { segments: Segment[] }): ReactElement {
  return (
    <>
      {segments.map((segment, i) =>
        segment.match ? (
          <span key={i} className="font-semibold text-strong">
            {segment.text}
          </span>
        ) : (
          <span key={i}>{segment.text}</span>
        ),
      )}
    </>
  )
}

function LocationName({ locationId, show }: { locationId?: string | null; show: boolean }): ReactElement | null {
  if (!show || !locationId) return null
  return <LazyLocationCell locationId={locationId} className="ml-auto shrink-0 truncate text-xs text-subtle" />
}

type RowProps = { query: PaletteQuery; showLocation: boolean }

export function ContactRowContent({ contact, query, showLocation }: RowProps & { contact: Contact }): ReactElement {
  const phone = formatPhoneNumber(contact.cellPhone ?? undefined)
  const stage = contact.lifecycleStage || contact.patientStatus
  return (
    <>
      <div className="min-w-0 grow">
        <div className="truncate text-sm text-strong">
          <Highlighted segments={splitByWords(contact.name, query.words)} />
        </div>
        <div className="truncate text-xs text-subtle">
          {phone && <Highlighted segments={splitByDigits(phone, query.digits)} />}
          {phone && stage && ` · `}
          {stage && startCase(stage)}
        </div>
      </div>
      <LocationName locationId={contact.locationId} show={showLocation} />
    </>
  )
}

export function ActivityRowContent({ activity, query, showLocation }: RowProps & { activity: Activity }): ReactElement {
  const typeLabel = activity.subType === `outbound` ? `${activity.type} (outbound)` : activity.type
  return (
    <>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fill-neutral p-1">
        {getActivityTypeIcon(activity.type, activity.subType ?? undefined)}
      </span>
      <div className="min-w-0 grow">
        <div className="truncate text-sm text-strong">
          <Highlighted segments={splitByWords(activityPersonLabel(activity), query.words)} />
        </div>
        <div className="truncate text-xs text-subtle">
          {typeLabel} · {activityWhenLabel(activity.activityDate ?? activity.createdDate)}
        </div>
      </div>
      <LocationName locationId={activity.locationId} show={showLocation} />
    </>
  )
}

export function SeeAllRowContent({ query }: { query: string }): ReactElement {
  return <span className="text-sm text-blue-600">See all activities for “{query}”</span>
}

/** Offered when a search finds nobody: the typed text becomes the new record's heading. */
export function CreateContactRowContent({ query }: { query: string }): ReactElement {
  return (
    <>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-blue-600">
        <UserPlusIcon />
      </span>
      <span className="truncate text-sm text-blue-600">Create contact “{query}”</span>
    </>
  )
}

export function PageRowContent({ page }: { page: PageRow }): ReactElement {
  const Icon = page.icon
  return (
    <>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-subtle">{Icon && <Icon />}</span>
      <span className="truncate text-sm text-strong">
        {page.breadcrumb && <span className="text-subtle">{page.breadcrumb} · </span>}
        {page.label}
      </span>
    </>
  )
}

export function ThreadRowContent({ thread, query, showLocation }: RowProps & { thread: PaletteThread }): ReactElement {
  const { message, contact } = thread
  const phone = formatPhoneNumber(counterpartPhone(message))
  const primary = contact?.name ?? phone ?? `Unknown number`
  return (
    <>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fill-neutral p-1">
        <ChatLinesIcon className="h-full w-full" color="#FFFFFF" />
      </span>
      <div className="min-w-0 grow">
        <div className="truncate text-sm text-strong">
          <Highlighted segments={contact ? splitByWords(primary, query.words) : splitByDigits(primary, query.digits)} />
        </div>
        <div className="truncate text-xs text-subtle">
          {messageSnippet(message.text)} · {activityWhenLabel(message.createdDate)}
        </div>
      </div>
      <LocationName locationId={message.locationId} show={showLocation} />
    </>
  )
}
